import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { signUp } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import awsConfig from '../../../amplify_outputs.json';
import TextFieldAuth from '../../../components/custom/TextFieldAuth';
import ConfirmationSent from './ConfirmationSent';

interface SignUpFormValues {
  fullName: string;
  payment: string;
  agentId: string;
  userId: string;
  phone: string;
  email: string;
  password: string;
}

const client = generateClient<Schema>();

const AgentSignUp: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatPhoneNumberToE164 = (phone: string): string => {
    if (phone.startsWith('0')) {
      return `+972${phone.slice(1)}`;
    }
    return phone;
  };

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const [ads, setAds] = useState(false);

  const [isLoading, setIsLoading] = React.useState(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState(false);

  const {
    handleSubmit,
    control,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>();

  const email = watch('email');

  const onSubmit = async (data: SignUpFormValues) => {
    console.log('SignUp onSubmit called with data:', data);
    setIsLoading(true);

    const formattedPhone = formatPhoneNumberToE164(data.phone);

    try {
      const signUpResponse = await signUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            phone_number: formattedPhone,
            name: data.fullName,
            'custom:role': 'agent',
          },
          clientMetadata: {
            confirmationUrl: awsConfig.custom.confirmVerificationFunctionUrl,
          },
        },
      });

      const response = await client.models.User.create({
        id: signUpResponse.userId,
        fullName: data.fullName || null,
        agentId: data.agentId || '',
        userId: data.userId || '',
        email: data.email || '',
        phone: data.phone || '',
        role: 'agent',
        createdAt: new Date().toISOString(),
      });

      console.log('SignUp response:', response);

      setConfirmationDialogOpen(true);
    } catch (error: any) {
      console.error('SignUp error:', error);
      setError('email', {
        type: 'manual',
        message: error.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setConfirmationDialogOpen(false);
    navigate('/login');
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '25px',
        background: 'transparent',
        my: '20px',
        gap: '18px',
        borderRadius: '20px',
        maxWidth: '600px',
      }}
      autoComplete="on"
    >
      <Box
        component="img"
        src="/LogoAuth.svg"
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '300px',
          height: 'auto',
        }}
      />
      <Stack justifyContent={'flex-end'}>
        <Typography
          fontWeight={'800'}
          fontSize={'62px'}
          fontFamily={'Heebo'}
          textAlign={'right'}
          color={theme.palette.white.main}
          dir="rtl"
        >
          ברוכים הבאים!
        </Typography>
        <Typography
          fontWeight={'300'}
          fontSize={'32px'}
          fontFamily={'Heebo'}
          textAlign={'right'}
          color={theme.palette.white.main}
          dir="rtl"
        >
          הופכים את תהליך הפקדות הפנסיה לפשוט ויעיל מאי פעם!
        </Typography>
        <Typography
          fontWeight={'300'}
          fontSize={'25px'}
          fontFamily={'Heebo'}
          textAlign={'right'}
          color={theme.palette.white.main}
          dir="rtl"
        >
          מדי חודש, תקבלו לווטסאפ את כל התשלומים לאישור מהיר - אנחנו נדאג לכל
          השאר
        </Typography>
      </Stack>
      <Stack
        width={'100%'}
        direction="row"
        justifyContent={'space-between'}
        spacing={4}
      >
        <Stack spacing={3.5} width={'100%'}>
          <Controller
            name="userId"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              pattern: {
                value: /^[0-9]+$/,
                message: 'מספר ח.פ. אינו תקין',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="userId"
                label="ת״ז"
                fullWidth
                required
                error={!!errors.userId}
                helperText={errors.userId ? errors.userId.message : ''}
                onBlur={() => trigger('userId')}
              />
            )}
          />

          <Controller
            name="agentId"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              pattern: {
                value: /^[0-9]+$/,
                message: 'מספר ח.פ. אינו תקין',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="agentId"
                label="מספר סוכן"
                fullWidth
                required
                error={!!errors.agentId}
                helperText={errors.agentId ? errors.agentId.message : ''}
                onBlur={() => trigger('agentId')}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'כתובת המייל אינה תקינה',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="email"
                label="מייל"
                fullWidth
                required
                autoComplete="email"
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
                onBlur={() => trigger('email')}
              />
            )}
          />
        </Stack>
        <Stack spacing={3.5} width={'100%'}>
          <Controller
            name="fullName"
            control={control}
            rules={{
              required: 'חובה למלא את השדה',
            }}
            defaultValue=""
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="fullName"
                label="שם מלא"
                fullWidth
                required
                error={!!errors.fullName}
                helperText={errors.fullName ? errors.fullName.message : ''}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              pattern: {
                value: /^(0\d{8,9}|\+972\d{8,9})$/,
                message: 'נא למלא מספר טלפון תקין',
              },
              minLength: {
                value: 9,
                message: 'נא למלא לפחות 9 תווים',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="phone"
                label="טלפון"
                fullWidth
                required
                error={!!errors.phone}
                helperText={errors.phone ? errors.phone.message : ''}
                onBlur={() => trigger('phone')}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              minLength: {
                value: 8,
                message: 'הסיסמה חייבת להכיל לפחות 8 תווים',
              },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
                message:
                  'הסיסמה צריכה להכיל לפחות 8 תווים ולהכיל שילוב של אות גדולה, אות קטנה וספרות',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="password"
                label="יצירת סיסמה"
                fullWidth
                required
                type={showPassword ? 'text' : 'password'}
                autoComplete="password"
                error={!!errors.password}
                helperText={errors.password ? errors.password.message : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onBlur={() => trigger('password')}
              />
            )}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
          mt: '7px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            sx={{
              width: '52px',
              height: '52px',
              minWidth: '52px',
              minHeight: '52px',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              backgroundColor: '#FFFFFF33',
              padding: 0,
              borderRadius: '8px',
            }}
            type="submit"
            disabled={isLoading}
          >
            <img
              alt="auth"
              src="/icons/auth.svg"
              width={'22.3px'}
              height={'25.36px'}
            />
          </Button>
          <Typography color={theme.palette.white.main}>כניסה</Typography>
        </Box>

        <Typography
          sx={{ cursor: 'pointer' }}
          color={theme.palette.green.light}
          onClick={() => {
            navigate('/reset');
          }}
        >
          ?שכחת סיסמה
        </Typography>
      </Box>
      <Stack
        width={'100%'}
        direction="row-reverse"
        justifyContent="space-between"
      >
        <Box
          sx={{
            display: 'flex',
            textAlign: 'right',
            justifyContent: 'flex-end',
            gap: '5px',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="body1"
              fontWeight="fontWeightMedium"
              fontSize={16}
              color={theme.palette.blue.main}
            >
              ?כבר יש לך חשבון
            </Typography>
            <Typography
              variant="body1"
              fontWeight="fontWeightMedium"
              fontSize={16}
              color={theme.palette.blue.main}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                navigate('/login');
              }}
            >
              התחבר
            </Typography>
          </Box>
        </Box>
        <Stack spacing={1}>
          <Typography
            fontWeight="300"
            fontFamily="Heebo"
            fontSize={13}
            color={theme.palette.white.main}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            בלחיצה שלח אני מאשר את{' '}
            <Link
              href="https://www.pielapay.co.il/terms"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.white.main,
                textDecoration: 'underline',
              }}
            >
              תנאי שימוש
            </Link>{' '}
            ו
            <Link
              href="https://www.pielapay.co.il/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.white.main,
                textDecoration: 'underline',
              }}
            >
              מדיניות הפרטיות
            </Link>
          </Typography>
          <Stack direction="row-reverse" alignItems="center" spacing={0.5}>
            <Checkbox
              disableRipple
              size="small"
              sx={{
                '&.MuiCheckbox-root': {
                  padding: 0,
                },
                '& .MuiSvgIcon-root': {
                  borderRadius: 0,
                  color: theme.palette.green.light,
                },
                '&.Mui-checked .MuiSvgIcon-root': {
                  color: theme.palette.green.light,
                },
              }}
              checked={ads}
              onChange={(e) => setAds(e.target.checked)}
            />
            <Typography
              fontWeight={'300'}
              fontFamily={'Heebo'}
              fontSize={13}
              textAlign={'right'}
              color={theme.palette.white.main}
            >
              אני רוצה לקבל דיוור פרסומי: מבצעים, הצעות למוצרים ומידע לפני כולם
              על שירותים חדשים
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <ConfirmationSent
        open={confirmationDialogOpen}
        email={email}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default AgentSignUp;
